/**
 * Google Identity Services one-tap sign-in (login or signup).
 */
import React, { useEffect, useState } from 'react';

const GoogleSignInButton = ({ onSuccess, onError, disabled = false, label = 'Continue with Google' }) => {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const waitForGoogle = () => {
      if (window.google?.accounts?.oauth2) return;
      setTimeout(waitForGoogle, 100);
    };
    waitForGoogle();
  }, []);

  const handleClick = () => {
    if (!window.google?.accounts?.oauth2) {
      onError?.('Google sign-in is not available. Please refresh the page.');
      return;
    }

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      onError?.('Google OAuth is not configured. Please contact support.');
      return;
    }

    try {
      setLoading(true);
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'openid email profile',
        callback: async (tokenResponse) => {
          if (tokenResponse.error) {
            onError?.('Google sign-in was cancelled or failed.');
            setLoading(false);
            return;
          }
          if (tokenResponse.access_token) {
            try {
              await onSuccess(tokenResponse.access_token);
            } catch (err) {
              onError?.(err?.message || 'Google sign-in failed. Please try again.');
            } finally {
              setLoading(false);
            }
          } else {
            onError?.('Failed to get Google access token.');
            setLoading(false);
          }
        },
      });
      tokenClient.requestAccessToken({ prompt: 'consent' });
    } catch {
      onError?.('Failed to initialize Google sign-in. Please try again.');
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      disabled={disabled || loading}
      className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      onClick={handleClick}
    >
      {loading ? (
        <span>Signing in with Google...</span>
      ) : (
        <>
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          {label}
        </>
      )}
    </button>
  );
};

export default GoogleSignInButton;
